import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { getUnlockedSections, getCompletedSetupSteps } from '@/utils/businessAppApi';

interface BusinessSetupContextProps {
  setupProgress: number;
  completedSteps: string[];
  unlockedSections: Record<string, boolean>;
  updateProgress: (newProgress: number) => void;
  refreshProgress: () => Promise<void>;
  isLoading: boolean;
}

const BusinessSetupContext = createContext<BusinessSetupContextProps | undefined>(undefined);

interface BusinessSetupProviderProps {
  children: ReactNode;
}

// Create a debounce utility function
const debounce = <F extends (...args: any[]) => Promise<void>>(fn: F, ms = 300) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function(this: any, ...args: Parameters<F>): Promise<void> {
    return new Promise(resolve => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        resolve(fn.apply(this, args));
      }, ms);
    });
  };
};

export function BusinessSetupProvider({ children }: BusinessSetupProviderProps) {
  const [setupProgress, setSetupProgress] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [unlockedSections, setUnlockedSections] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  
  // Use ref to prevent stale data in debounced functions
  const stepsRef = useRef(completedSteps);
  const sectionsRef = useRef(unlockedSections);
  
  // Track if data has been loaded to prevent duplicate calls
  const initialDataLoaded = useRef(false);
  
  // Track ongoing requests to prevent duplicates
  const isRefreshing = useRef(false);

  useEffect(() => {
    stepsRef.current = completedSteps;
  }, [completedSteps]);

  useEffect(() => {
    sectionsRef.current = unlockedSections;
  }, [unlockedSections]);

  const calculateProgress = useCallback((
    steps: string[], 
    sections: Record<string, boolean>,
    totalSteps: number = 3 // Default to 3 setup steps
  ) => {
    // Count completed steps based on the setupSteps array from BusinessHomePage
    // For simplicity, use the common step keys known to the system
    const commonStepKeys = ['settings', 'security', 'hours'];
    
    // Count how many steps are completed either by being in the steps array
    // or by having the corresponding section enabled (for the 'settings' step)
    const completedCount = commonStepKeys.filter(key => 
      steps.includes(key) || 
      (key === 'settings' && sections[key] === true)
    ).length;
    
    // Calculate percentage - ensure we don't exceed 100%
    const percentage = Math.min(Math.round((completedCount / totalSteps) * 100), 100);
    return percentage;
  }, []);

  const refreshProgress = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isRefreshing.current) {
      return;
    }
    
    isRefreshing.current = true;
    setIsLoading(true);
    
    try {
      // Fetch unlocked sections
      const sectionsData = await getUnlockedSections();
      setUnlockedSections(sectionsData);

      // Fetch completed steps
      const stepsData = await getCompletedSetupSteps();
      setCompletedSteps(stepsData);

      // Calculate and set progress
      const progress = calculateProgress(stepsData, sectionsData);
      setSetupProgress(progress);
    } catch (error) {
      console.error('Error refreshing setup progress:', error);
      // Don't update state on error to prevent flickering
    } finally {
      setIsLoading(false);
      isRefreshing.current = false;
      initialDataLoaded.current = true;
    }
  }, [calculateProgress]);

  // Debounced version of refreshProgress to prevent API hammering
  const debouncedRefresh = useCallback(
    async () => {
      return debounce(refreshProgress, 500)();
    },
    [refreshProgress]
  );

  // Initial fetch of progress data - only once
  useEffect(() => {
    if (!initialDataLoaded.current) {
      refreshProgress();
    }
    
    return () => {
      // Clean up any pending debounced calls
      isRefreshing.current = false;
    };
  }, [refreshProgress]);

  // Listen for feature updates
  useEffect(() => {
    const handleFeaturesUpdate = (event: CustomEvent<Record<string, boolean>>) => {
      setUnlockedSections(event.detail);
      
      // Recalculate progress using the current state
      const progress = calculateProgress(stepsRef.current, event.detail);
      setSetupProgress(progress);
    };

    window.addEventListener('featuresUpdated', handleFeaturesUpdate as EventListener);

    return () => {
      window.removeEventListener('featuresUpdated', handleFeaturesUpdate as EventListener);
    };
  }, [calculateProgress]);

  // Update progress directly (rarely needed)
  const updateProgress = (newProgress: number) => {
    setSetupProgress(newProgress);
  };

  const value = {
    setupProgress,
    completedSteps,
    unlockedSections,
    updateProgress,
    refreshProgress: debouncedRefresh,
    isLoading
  };

  return (
    <BusinessSetupContext.Provider value={value}>
      {children}
    </BusinessSetupContext.Provider>
  );
}

export const useBusinessSetup = () => {
  const context = useContext(BusinessSetupContext);
  if (context === undefined) {
    throw new Error('useBusinessSetup must be used within a BusinessSetupProvider');
  }
  return context;
}; 